"use client";

import Link from "next/link";

import { analytics } from "@/lib/analytics";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";

// Menu item-modal footer. Owner.com puts an "Add to order" control in this slot.
// For Tuscan, this drives the owner's active goal once a guest is browsing the
// menu: start an online order, while keeping reservations as the secondary path.
function orderLinkLabel(url: string | undefined): string | null {
  if (!url) return null;
  if (/toasttab/i.test(url)) return "Order on Toast";
  if (/grubhub/i.test(url)) return "Order on Grubhub";
  if (url.startsWith("http")) return "Order online";
  return "Order pickup or delivery";
}

export function MenuReserveFooter({ itemName }: { itemName: string }) {
  const { siteConfig } = useMarketingLocationContext();
  const reservationUrl = siteConfig.reservationUrl;
  const orderUrl = siteConfig.orderUrl;
  const orderLabel = orderLinkLabel(orderUrl);
  const orderIsExternal = orderUrl?.startsWith("http") ?? false;
  const orderProps = orderIsExternal
    ? { target: "_blank" as const, rel: "noopener noreferrer" }
    : {};
  const reserveIsExternal = reservationUrl.startsWith("http");
  const reserveProps = reserveIsExternal
    ? { target: "_blank" as const, rel: "noopener noreferrer" }
    : {};

  return (
    // Self-handled: these CTAs call analytics.* directly in onClick, so the
    // /menu [data-track] delegation (MenuCtaAnalytics) must skip this subtree
    // or every click would double-count.
    <div className="mt-6 border-t border-[#ededed] pt-5" data-analytics-self-handled="true">
      <div className="rounded-[12px] border border-[var(--roma-parchment-line)] bg-white p-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--roma-terracotta)]">
          Ready to order?
        </p>
        <p className="mt-1 text-[15px] font-bold leading-snug text-[#0d0d0d]">
          Start your order with the {itemName}
        </p>
        <p className="mt-1 text-[13px] leading-5 text-[#4d4d4d]">
          Build your pickup or delivery order from the menu, then pick your time
          at checkout.
        </p>
        {orderUrl && orderLabel ? (
          orderIsExternal ? (
            <a
              href={orderUrl}
              {...orderProps}
              onClick={() =>
                analytics.orderStarted({
                  location: "menu_item_modal",
                  cta_text: orderLabel,
                  destination_url: orderUrl,
                })
              }
              data-track="order_started"
              data-track-location="menu_item_modal"
              data-track-label={itemName}
              className="mt-3 inline-flex h-11 w-full items-center justify-center rounded-[10px] bg-[var(--roma-terracotta)] text-[15px] font-semibold text-[#1a1a1a] transition-[transform,background-color] hover:bg-[var(--roma-saffron-deep)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-offset-2 focus-visible:ring-[color:var(--roma-terracotta)]"
            >
              {orderLabel}
            </a>
          ) : (
            <Link
              href={orderUrl}
              prefetch={false}
              onClick={() =>
                analytics.orderStarted({
                  location: "menu_item_modal",
                  cta_text: orderLabel,
                  destination_url: orderUrl,
                })
              }
              data-track="order_started"
              data-track-location="menu_item_modal"
              data-track-label={itemName}
              className="mt-3 inline-flex h-11 w-full items-center justify-center rounded-[10px] bg-[var(--roma-terracotta)] text-[15px] font-semibold text-[#1a1a1a] transition-[transform,background-color] hover:bg-[var(--roma-saffron-deep)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-offset-2 focus-visible:ring-[color:var(--roma-terracotta)]"
            >
              {orderLabel}
            </Link>
          )
        ) : null}
      </div>
      <Link
        href={reservationUrl}
        prefetch={false}
        {...reserveProps}
        onClick={() =>
          analytics.reservationClicked("menu_item_modal", {
            label: itemName,
            cta_text: "Reserve a table",
            destination_url: reservationUrl,
          })
        }
        data-track="reservation_clicked"
        data-track-location="menu_item_modal"
        data-track-label={itemName}
        className="mt-3 inline-flex items-center gap-1 rounded text-[13px] font-semibold text-[#4d4d4d] underline-offset-4 transition-colors hover:text-[var(--roma-terracotta)] hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[color:var(--roma-terracotta)]"
      >
        Reserve a table
      </Link>
    </div>
  );
}

export default MenuReserveFooter;
